import axios from 'axios';

const cricketAPI = axios.create({
    baseURL: '/api/cricket',
    headers: {
        'Content-Type': 'application/json'
    }
});

const SCORE_WIDGET_URL = 'https://odd.o99hub.com/ws/getScoreData';
const ODDS_FEED_URL = 'https://d1gu1gyqlc4qng.cloudfront.net/exchange/v1/fi-ops/getFeed';
const FANCY_EVENT_DETAILS_URL = 'https://deoulnk5dqcl0.cloudfront.net/exchange/v1/dashboard/getFancyEventDetails';

const getMarketPriceLevels = (oddsList, volumeList, fallbackOdds) => {
    if (Array.isArray(oddsList)) {
        return oddsList.map((odds, index) => {
            const price = Number(odds);
            const size = Number(volumeList?.[index]);
            return {
                price: Number.isFinite(price) ? price : 0,
                size: Number.isFinite(size) ? size : 0
            };
        });
    }

    const price = Number(fallbackOdds);
    return [{
        price: Number.isFinite(price) ? price : 0,
        size: 0
    }];
};

const getFancyMarketStatus = (market, runner) => {
    if (
        market.ballRunning ||
        runner.rnrBallRunning ||
        runner.backBallRunning ||
        runner.layBallRunning
    ) {
        return 'Ball Running';
    }

    if (
        market.status !== 'OPEN' ||
        (runner.rnrStatus && runner.rnrStatus !== 'ACTIVE') ||
        (runner.layStatus && runner.layStatus !== 'ACTIVE') ||
        runner.rnrSuspended ||
        runner.backRnrSuspended ||
        runner.layRnrSuspended
    ) {
        return 'SUSPENDED';
    }

    return 'ACTIVE';
};

const normalizeFancyRunner = (market, runner, name = runner.runnerName) => {
    const status = getFancyMarketStatus(market, runner);
    return {
        name: name || `Selection ${runner.selectionId}`,
        runnerName: name || `Selection ${runner.selectionId}`,
        RunnerName: name || `Selection ${runner.selectionId}`,
        selectionId: runner.selectionId,
        SelectionId: runner.selectionId,
        marketId: market.marketId,
        marketName: market.marketType,
        marketType: market.marketType,
        status,
        GameStatus: status,
        min: market.minStake,
        max: market.maxStake,
        minBet: market.minStake,
        maxBet: market.maxStake,
        back: getMarketPriceLevels(runner.backOddsList, runner.backVolList, runner.backOdds),
        lay: getMarketPriceLevels(runner.layOddsList, runner.layVolList, runner.layOdds),
        BackPrice1: Number(runner.backOdds) || 0,
        LayPrice1: Number(runner.layOdds) || 0,
        BackSize1: Number(runner.backVolList?.[0]) || 0,
        LaySize1: Number(runner.layVolList?.[0]) || 0,
        backRuns: runner.backRuns,
        layRuns: runner.layRuns,
        runs: runner.runs,
        rnrStatus: runner.rnrStatus,
        rnrSuspended: runner.rnrSuspended,
        ballRunning: market.ballRunning
    };
};

const normalizeFancyEventDetails = (responseData, eventId) => {
    if (responseData?.status !== 'OK' || !Array.isArray(responseData.data)) {
        throw new Error('Invalid fancy event details response');
    }

    const markets = responseData.data.flatMap(group =>
        Array.isArray(group?.markets) ? group.markets : []
    ).filter(market => String(market.eventId) === String(eventId));

    const bookmakerMarkets = [];
    const bm1Markets = [];
    const sessionMarkets = [];
    let tiedMatchMarkets = { runners: [] };

    markets.forEach(market => {
        const runners = Array.isArray(market.runners) ? market.runners : [];

        if (market.fancyCategory === 'Fancy_Market') {
            runners.forEach(runner => {
                const normalized = normalizeFancyRunner(market, runner, market.marketType);
                const backPrice = Number(runner.backRuns) || 0;
                const layPrice = Number(runner.layRuns) || 0;
                const backSize = Number(runner.backOdds) || 0;
                const laySize = Number(runner.layOdds) || 0;

                sessionMarkets.push({
                    ...normalized,
                    RunnerName: market.marketType,
                    marketName: market.marketType,
                    BackPrice1: backPrice,
                    LayPrice1: layPrice,
                    BackSize1: backSize,
                    LaySize1: laySize,
                    back: [{ price: backPrice, size: backSize }],
                    lay: [{ price: layPrice, size: laySize }],
                    backPrice1: backPrice,
                    layPrice1: layPrice,
                    backSize1: backSize,
                    laySize1: laySize
                });
            });
            return;
        }

        if (market.fancyCategory !== 'Bookmaker_Market') {
            return;
        }

        if (market.marketType === 'Tied Match') {
            tiedMatchMarkets = {
                marketInfo: {
                    marketId: market.marketId,
                    marketName: market.marketType,
                    status: market.status,
                    inplay: !market.ballRunning,
                    maxBet: market.maxStake,
                    min: market.minStake
                },
                runners: runners.map(runner => ({
                    ...normalizeFancyRunner(
                        market,
                        runner,
                        runner.runnerName?.toLowerCase() === 'yes'
                            ? 'Yes'
                            : runner.runnerName?.toLowerCase() === 'no'
                                ? 'No'
                                : runner.runnerName
                    ),
                    name: runner.runnerName?.toLowerCase() === 'yes'
                        ? 'Yes'
                        : runner.runnerName?.toLowerCase() === 'no'
                            ? 'No'
                            : runner.runnerName
                }))
            };
            return;
        }

        if (market.marketType === 'Bookmaker 0%') {
            runners.forEach(runner => {
                bookmakerMarkets.push(normalizeFancyRunner(market, runner));
            });
            return;
        }

        runners.forEach(runner => {
            const normalized = normalizeFancyRunner(market, runner);
            bm1Markets.push({
                ...normalized,
                RunnerName: normalized.name,
                BackPrice1: normalized.back[0]?.price || 0,
                LayPrice1: normalized.lay[0]?.price || 0,
                BackSize1: normalized.back[0]?.size || 0,
                LaySize1: normalized.lay[0]?.size || 0
            });
        });
    });

    return {
        bookmakerMarkets,
        bm1Markets,
        tiedMatchMarkets,
        sessionMarkets
    };
};

export const getTeamNames = (matchName) => {
    const name = typeof matchName === 'string' ? matchName.trim() : '';
    const teams = name.split(/\s+vs?\.?\s+/i);

    return {
        team1: teams[0]?.trim() || 'Team 1',
        team2: teams[1]?.trim() || 'Team 2'
    };
};

const getCricketMatches = (responseData) => {
    if (Array.isArray(responseData)) {
        return responseData;
    }

    if (Array.isArray(responseData?.sport?.body)) {
        return responseData.sport.body;
    }

    if (Array.isArray(responseData?.data?.events)) {
        return responseData.data.events;
    }

    const categories = responseData?.data?.children;
    if (Array.isArray(categories)) {
        const cricketCategory = categories.find(category =>
            String(category?.data?.id) === '4' ||
            category?.data?.name?.toLowerCase() === 'cricket'
        );

        if (Array.isArray(cricketCategory?.children)) {
            return cricketCategory.children;
        }
    }

    throw new Error('Invalid cricket matches response');
};

const normalizeMatchesResponse = (responseData) => {
    const matches = getCricketMatches(responseData)
        .filter(match =>
            match && String(match.event_type_id ?? match.eventTypeId) === '4'
        )
        .map(match => {
            const runners = Array.isArray(match.runners) ? match.runners : [];
            const getPrice = (runner, key) => {
                const price = Number(runner?.[key]?.[0]?.pr);
                return Number.isFinite(price) ? price : 0;
            };

            return {
                ...match,
                marketid: match.marketid ?? match.marketId ?? match.market_id,
                matchid: match.matchid ?? match.eventId ?? match.event_id,
                matchName: match.matchName ?? match.eventName ?? match.name,
                cname: match.cname ?? match.competitionName ?? match.competition_name ?? '',
                matchdate: match.matchdate ?? match.marketTime ?? match.open_date_format ?? match.open_date,
                inPlay: match.inPlay === true || match.in_play === true ||
                    match.in_play === 1 || match.in_play === '1',
                status: match.suspended ? 'SUSPENDED' : (match.status || 'OPEN'),
                b1: match.b1 ?? getPrice(runners[0], 'btb'),
                l1: match.l1 ?? getPrice(runners[0], 'btl'),
                b2: match.b2 ?? getPrice(runners[1], 'btb'),
                l2: match.l2 ?? getPrice(runners[1], 'btl')
            };
        });

    return {
        ...responseData,
        sport: {
            ...responseData?.sport,
            body: matches
        },
        sportId: responseData?.sportId ?? 4,
        eventNotIncluded: responseData?.eventNotIncluded ?? []
    };
};

cricketAPI.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        console.log('📡 Cricket API Request:', config.url);
        return config;
    },
    (error) => Promise.reject(error)
);

cricketAPI.interceptors.response.use(
    (response) => {
        console.log('✅ Cricket API Response:', response.status);
        return response;
    },
    (error) => {
        console.error('❌ Cricket API Error:', error.response?.status || error.message);
        return Promise.reject(error);
    }
);

export const cricketService = {
    getAllMatches: async () => {
        try {
            const response = await cricketAPI.get('/live-matches');
            return normalizeMatchesResponse(response.data);
        } catch (error) {
            console.error('Error fetching matches:', error);
            throw error;
        }
    },

    getLiveMatches: async () => {
        try {
            const response = await cricketAPI.get('/live-only');
            return normalizeMatchesResponse(response.data);
        } catch (error) {
            console.error('Error fetching live matches:', error);
            throw error;
        }
    },

    getMatchOdds: async (marketId, matchName) => {
        try {
            const response = await axios.get(ODDS_FEED_URL, {
                params: {
                    src: 'SATSP',
                    etid: '4',
                    marketIds: marketId
                },
                headers: {
                    Accept: 'application/json, text/plain, */*',
                    'Content-Type': 'application/json',
                    'X-Timezone': 'America/New_York',
                    Authorization: 'Bearer null',
                    'Tenant-Id': '20230531',
                    'Cache-Control': 'no-store, no-cache, must-revalidate, proxy-revalidate',
                    Pragma: 'no-cache',
                    Expires: '0'
                }
            });
            const feed = response.data;
            if (feed?.status !== 'OK' || !Array.isArray(feed.data)) {
                throw new Error('Invalid cricket odds response');
            }

            const market = feed.data.find(item =>
                String(item.market_id ?? item.exch_market_id ?? item.betfair_data?.marketId) === String(marketId)
            ) || feed.data[0];
            const betfairMarket = market?.betfair_data;
            if (!Array.isArray(betfairMarket?.runners)) {
                throw new Error('Cricket odds response does not contain market runners');
            }

            const fallbackNames = getTeamNames(matchName);
            const suppliedNames = Array.isArray(market.runners_name)
                ? market.runners_name
                : [];
            const teamNames = [fallbackNames.team1, fallbackNames.team2];
            const marketStatus = betfairMarket.status === 'OPEN' ? 'ACTIVE' : 'SUSPENDED';

            return betfairMarket.runners.map((runner, index) => {
                const suppliedName = suppliedNames[index];
                const name = typeof suppliedName === 'string'
                    ? suppliedName
                    : suppliedName?.name || suppliedName?.runnerName;
                const status = marketStatus === 'ACTIVE' && runner.status === 'ACTIVE'
                    ? 'ACTIVE'
                    : 'SUSPENDED';
                const toPrices = prices => Array.isArray(prices)
                    ? prices.map(level => ({
                        price: level.price,
                        size: level.size
                    }))
                    : [];

                return {
                    name: name || teamNames[index] || `Runner ${index + 1}`,
                    runnerName: name || teamNames[index] || `Runner ${index + 1}`,
                    selectionId: runner.selectionId,
                    status,
                    GameStatus: status,
                    marketId: betfairMarket.marketId || market.market_id || market.exch_market_id,
                    marketName: 'Match Odds',
                    marketType: betfairMarket.marketType || betfairMarket.mType || 'match_odds',
                    inplay: betfairMarket.inPlay,
                    totalMatched: betfairMarket.totalMatched,
                    back: toPrices(runner.price?.back),
                    lay: toPrices(runner.price?.lay)
                };
            });
        } catch (error) {
            console.error('Error fetching match odds directly from the provider:', error);
            throw error;
        }
    },

    getFancyEventDetails: async (eventId) => {
        try {
            const response = await axios.get(FANCY_EVENT_DETAILS_URL, {
                params: { eventId },
                headers: {
                    Accept: 'application/json, text/plain, */*',
                    'Content-Type': 'application/json',
                    'X-Timezone': 'America/New_York',
                    Authorization: 'Bearer null',
                    'Tenant-Id': '20230531',
                    'Cache-Control': 'no-store, no-cache, must-revalidate, proxy-revalidate',
                    Pragma: 'no-cache',
                    Expires: '0'
                }
            });
            return normalizeFancyEventDetails(response.data, eventId);
        } catch (error) {
            console.error('Error fetching bookmaker, tied-match, and session markets:', error);
            throw error;
        }
    },

    getScoreWidget: async (eventId) => {
        const token = process.env.REACT_APP_SCORE_WIDGET_TOKEN?.trim();
        if (!token) {
            throw new Error('REACT_APP_SCORE_WIDGET_TOKEN must be configured to fetch live scores');
        }

        try {
            const body = new URLSearchParams({ event_id: String(eventId) });
            const response = await axios.post(SCORE_WIDGET_URL, body, {
                headers: {
                    Accept: 'application/json, text/plain, */*',
                    'Content-Type': 'application/x-www-form-urlencoded',
                    Authorization: `Bearer ${token.replace(/^bearer\s+/i, '')}`
                },
                responseType: 'text'
            });
            if (typeof response.data !== 'string') {
                throw new Error('Score widget API did not return HTML text');
            }
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    getStatus: async () => {
        try {
            const response = await cricketAPI.get('/status');
            return response.data;
        } catch (error) {
            console.error('Error checking status:', error);
            throw error;
        }
    }
};

export default cricketService;